import React, { Component } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SharedFunctionContext } from '../../utilities/SharedFunctionContext';

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

class Log extends Component {
  static contextType = SharedFunctionContext;

  constructor(props) {
    super(props);
    this.state = {
      logs: [],
      groupedLogs: [],
      userDetails: {},
      loading: true,
      error: null,
    };
  }

  componentDidMount() {
    this.fetchLogs();
  }

  fetchLogs = async () => {
    try {
      const response = await axios.get(`${EndPoint}/log`, { withCredentials: true });
      const logs = Array.isArray(response.data) ? response.data : [];
      const groupedLogs = this.groupSortAndSummariseLogs(logs);

      this.setState(
        {
          logs,
          groupedLogs,
          loading: false,
          error: null,
        },
        () => {
          this.fetchUserDetails(groupedLogs);
        }
      );
    } catch (error) {
      console.error('Error fetching logs:', error);
      this.setState({
        error: error.message || 'Failed to fetch logs',
        loading: false,
      });
    }
  };

  // Adjust this if your actual login log type is different
  isLoginType = (type = '') => {
    const normalised = String(type).toLowerCase().trim();
    return (
      normalised === 'login' ||
      normalised === 'log in' ||
      normalised === 'signed in' ||
      normalised === 'signin' ||
      normalised === 'sign in'
    );
  };

  groupSortAndSummariseLogs = (logs) => {
    const grouped = logs.reduce((acc, log) => {
      const { userId, type, timeStamp } = log;
      if (!userId) return acc;

      if (!acc[userId]) {
        acc[userId] = [];
      }

      acc[userId].push({
        type,
        timeStamp,
      });

      return acc;
    }, {});

    const result = Object.entries(grouped).map(([userId, activity]) => {
      const sortedActivity = [...activity].sort(
        (a, b) => new Date(b.timeStamp) - new Date(a.timeStamp)
      );

      const loginActivities = sortedActivity.filter((item) =>
        this.isLoginType(item.type)
      );

      const lastLogin = loginActivities.length > 0 ? loginActivities[0].timeStamp : null;

      return {
        userId,
        activity: sortedActivity,
        loginCount: loginActivities.length,
        lastLogin,
        totalActivityCount: sortedActivity.length,
      };
    });

    // Sort by latest login descending. Users without login go to bottom.
    return result.sort((a, b) => {
      if (!a.lastLogin && !b.lastLogin) return 0;
      if (!a.lastLogin) return 1;
      if (!b.lastLogin) return -1;
      return new Date(b.lastLogin) - new Date(a.lastLogin);
    });
  };

  fetchUserDetails = async (groupedLogs) => {
    try {
      const userRequests = groupedLogs.map(({ userId }) =>
        axios
          .get(`${EndPoint}/user/${userId}`, { withCredentials: true })
          .then((response) => ({
            userId,
            data: response.data,
          }))
          .catch((error) => {
            console.error(`Error fetching user details for ${userId}:`, error);
            return {
              userId,
              data: null,
            };
          })
      );

      const results = await Promise.all(userRequests);

      const userDetails = results.reduce((acc, { userId, data }) => {
        if (data) {
          acc[userId] = data;
        }
        return acc;
      }, {});

      this.setState({ userDetails });
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  addCredit = async (creditPoint, userId) => {
    try {
      const response = await axios.post(
        `${EndPoint}/user/credits`,
        { creditPoint, userId },
        { withCredentials: true }
      );

      toast.success(`${creditPoint} credits added successfully`, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 500,
        hideProgressBar: false,
      });

      this.fetchLogs();

      const { sharedFunction } = this.context;
      if (sharedFunction && response?.data?.credits !== undefined) {
        sharedFunction(response.data.credits);
      }
    } catch (error) {
      console.error(
        `Error adding credit for userId: ${userId}`,
        error.response?.data || error.message
      );

      toast.error('Failed to add credits', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 800,
      });
    }
  };

  formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };

  renderSummaryCards() {
    const { groupedLogs } = this.state;

    const totalUsers = groupedLogs.length;
    const totalLogins = groupedLogs.reduce((sum, item) => sum + item.loginCount, 0);
    const activeUsers = groupedLogs.filter((item) => item.loginCount > 0).length;

    const cards = [
      { label: 'Total Users', value: totalUsers },
      { label: 'Total Logins', value: totalLogins },
      { label: 'Users With Login', value: activeUsers },
    ];

    return (
      <div className="grid grid-cols-3 gap-4 md:grid-cols-3 mb-6 xs:grid-cols-1 sm:grid-cols-1">
        {cards.map((card) => (
          <div
            key={card.label}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <p className="text-sm text-slate-500">{card.label}</p>
            <p className="mt-2 text-3xl font-bold text-slate-800">{card.value}</p>
          </div>
        ))}
      </div>
    );
  }

  renderTable() {
    const { groupedLogs, userDetails } = this.state;

    return (
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1200px]">
            <thead className="bg-slate-50">
              <tr className="text-left text-sm text-slate-600">
                <th className="px-4 py-4 font-semibold">User</th>
                <th className="px-4 py-4 font-semibold">Email</th>
                <th className="px-4 py-4 font-semibold">Credits</th>
                <th className="px-4 py-4 font-semibold">Login Count</th>
                <th className="px-4 py-4 font-semibold">Last Login</th>
                <th className="px-4 py-4 font-semibold">Recent Activity</th>
                <th className="px-4 py-4 font-semibold text-center">Gift Credits</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {groupedLogs.map(
                ({ userId, activity, loginCount, lastLogin, totalActivityCount }) => {
                  const user = userDetails[userId];

                  return (
                    <tr key={userId} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col">
                          <span className="font-semibold text-slate-800">
                            {user
                              ? `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                                'Unnamed User'
                              : 'Loading...'}
                          </span>
                          <span className="text-xs text-slate-500 break-all">{userId}</span>
                        </div>
                      </td>

                      <td className="px-4 py-4 align-top text-slate-700">
                        {user ? user.email : 'Loading...'}
                      </td>

                      <td className="px-4 py-4 align-top">
                        <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700">
                          {user ? user.credits ?? 0 : 'Loading...'}
                        </span>
                      </td>

                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col">
                          <span className="text-lg font-bold text-slate-800">{loginCount}</span>
                          <span className="text-xs text-slate-500">
                            {totalActivityCount} total activities
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-4 align-top text-slate-700">
                        {this.formatDate(lastLogin)}
                      </td>

                      <td className="px-4 py-4 align-top">
                        <div className="max-h-40 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3">
                          {activity.length === 0 ? (
                            <p className="text-sm text-slate-500">No activity</p>
                          ) : (
                            <ul className="space-y-2">
                              {activity.slice(0, 12).map((act, index) => (
                                <li
                                  key={`${act.timeStamp}-${index}`}
                                  className="flex flex-wrap gap-2 text-sm"
                                >
                                  <span className="rounded-md bg-sky-100 px-2 py-1 text-sky-800">
                                    {act.type}
                                  </span>
                                  <span className="rounded-md bg-white px-2 py-1 text-slate-600 border border-slate-200">
                                    {this.formatDate(act.timeStamp)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col items-center gap-2">
                          <button
                            onClick={() => this.addCredit(100, userId)}
                            className="w-24 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white shadow hover:bg-sky-700"
                          >
                            +100
                          </button>

                          <button
                            onClick={() => this.addCredit(500, userId)}
                            className="w-24 rounded-lg bg-amber-400 px-3 py-2 text-sm font-medium text-slate-900 shadow hover:bg-amber-500"
                          >
                            +500
                          </button>

                          <button
                            onClick={() => this.addCredit(1000, userId)}
                            className="w-24 rounded-lg bg-rose-500 px-3 py-2 text-sm font-medium text-white shadow hover:bg-rose-600"
                          >
                            +1,000
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  render() {
    const { groupedLogs, loading, error } = this.state;

    return (
      <div className="min-h-screen bg-slate-100 p-4 md:p-6">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6 flex flex-col gap-2">
            <h1 className="text-5xl text-center my-4 font-bold tracking-tight text-sky-900">
              User Activity Dashboard
            </h1>
       
          </div>

          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
              <p className="text-slate-500">Loading...</p>
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-8 shadow-sm">
              <p className="text-red-600">Error: {error}</p>
            </div>
          ) : groupedLogs.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
              <p className="text-slate-500">No logs available.</p>
            </div>
          ) : (
            <>
              {this.renderSummaryCards()}
              {this.renderTable()}
            </>
          )}
        </div>

        <ToastContainer />
      </div>
    );
  }
}

export default Log;